import { useEffect, useState } from 'react';
import { accessoryAPI, vehicleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, Input, Select, Loading, Alert } from './ui';
import { formatRs } from '../utils/currency';

// Standard checklist — matches the vehicle tool/accessory sheet
export const STANDARD_ITEMS = [
  'Jack', 'Wheel Brace', 'Other Tools', 'Mini Air Compressor',
  'Umbrella', 'Carpet', 'Setup', 'Seat Cover',
];

const COND = {
  good:    'bg-green-100 text-green-700',
  damaged: 'bg-amber-100 text-amber-700',
  missing: 'bg-red-100 text-red-700',
};

export default function VehicleAccessories() {
  const { can } = useAuth();
  const [items,    setItems]    = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [saving,   setSaving]   = useState(false);
  const [filterVehicle, setFilterVehicle] = useState('');
  const [expanded, setExpanded] = useState({});

  const [modal, setModal] = useState(false);
  const [vehicleId, setVehicleId] = useState('');
  const [rows, setRows] = useState([]);      // checklist rows
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([accessoryAPI.getAll(), vehicleAPI.getAll({ limit: 200 })])
      .then(([a, v]) => {
        setItems(a.data.data || []);
        setVehicles(v.data.data || []);
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => {
    setVehicleId('');
    setRows(STANDARD_ITEMS.map(name => ({
      checked: false, item_name: name, quantity: 1,
      branded: false, brand_name: '', amount: '', condition_st: 'good',
    })));
    setModal(true);
  };

  const setRow = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addCustomRow = () => setRows(rs => [...rs, {
    checked: true, item_name: '', quantity: 1, branded: false, brand_name: '', amount: '', condition_st: 'good',
  }]);

  const save = async (e) => {
    e.preventDefault();
    if (!vehicleId) return setError('Select a vehicle first');
    const selected = rows.filter(r => r.checked && r.item_name.trim());
    if (!selected.length) return setError('Tick at least one item');
    setSaving(true);
    try {
      await accessoryAPI.create({
        vehicle_id: vehicleId,
        items: selected.map(r => ({
          item_name: r.item_name.trim(),
          quantity: Number(r.quantity) || 1,
          branded: !!r.branded,
          brand_name: r.branded ? (r.brand_name || null) : null,
          amount: r.amount || null,
          condition_st: r.condition_st || 'good',
          issued_date: new Date().toISOString().split('T')[0],
        })),
      });
      flash(`${selected.length} item(s) added`);
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await accessoryAPI.update(editing.id, editForm);
      flash('Item updated'); setEditing(null); load();
    } catch (err) { setError(err.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Remove "${it.item_name}" from ${it.vehicle?.reg_number}?`)) return;
    try { await accessoryAPI.remove(it.id); flash('Item removed'); load(); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  };

  // Group by vehicle
  const groups = items
    .filter(i => !filterVehicle || String(i.vehicle_id) === filterVehicle)
    .reduce((acc, i) => {
      const key = i.vehicle_id;
      (acc[key] = acc[key] || { vehicle: i.vehicle, items: [] }).items.push(i);
      return acc;
    }, {});

  return (
    <div className="space-y-4">
      {error   && <Alert type="error"   message={error} />}
      {success && <Alert type="success" message={success} />}

      <div className="flex gap-3 flex-wrap items-center">
        <select value={filterVehicle} onChange={e => setFilterVehicle(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All vehicles</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number}</option>)}
        </select>
        <p className="text-xs text-gray-400">
          {Object.keys(groups).length} vehicle(s) · {items.length} item(s) ·
          value {formatRs(items.reduce((t, i) => t + Number(i.amount || 0), 0))}
        </p>
        {can('admin','transport_officer','manager') && (
          <Button className="ml-auto" onClick={openAdd}>+ Add Items to Vehicle</Button>
        )}
      </div>

      {loading ? <Loading /> : Object.keys(groups).length === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          No vehicle accessories recorded yet — add items to a vehicle to start
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groups).map(([vid, g]) => {
            const open = expanded[vid] !== false;   // default open
            return (
              <div key={vid} className="bg-white rounded-xl border overflow-hidden">
                <button onClick={() => setExpanded(e => ({ ...e, [vid]: !open }))}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">{g.vehicle?.reg_number || `Vehicle #${vid}`}</span>
                    <span className="text-sm text-gray-500">{g.vehicle?.brand} {g.vehicle?.model}</span>
                    <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs">{g.items.length} items</span>
                    {g.items.some(i => i.condition_st === 'missing') && (
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">missing items</span>
                    )}
                  </div>
                  <span className="text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
                </button>
                {open && (
                  <table className="w-full text-sm">
                    <thead className="bg-white text-gray-500 text-xs uppercase border-b">
                      <tr>{['Item','Qty','Branded','Amount','Condition','Issued','Actions'].map(h =>
                        <th key={h} className="px-4 py-2 text-left font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {g.items.map(i => (
                        <tr key={i.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{i.item_name}
                            {i.notes && <p className="text-xs text-gray-400">{i.notes}</p>}</td>
                          <td className="px-4 py-2">{i.quantity}</td>
                          <td className="px-4 py-2 text-xs">
                            {i.branded ? <span className="text-green-700">Yes{i.brand_name ? ` — ${i.brand_name}` : ''}</span> : <span className="text-gray-400">No</span>}
                          </td>
                          <td className="px-4 py-2 text-xs">{i.amount ? formatRs(i.amount) : '—'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${COND[i.condition_st] || ''}`}>{i.condition_st}</span>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-400">{i.issued_date || '—'}</td>
                          <td className="px-4 py-2">
                            {can('admin','transport_officer','manager') && (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" title="Edit"
                                  onClick={() => { setEditing(i); setEditForm({
                                    item_name: i.item_name, quantity: i.quantity, branded: !!i.branded,
                                    brand_name: i.brand_name || '', amount: i.amount || '',
                                    condition_st: i.condition_st, notes: i.notes || '' }); }}>✏️</Button>
                                {can('admin','transport_officer') && (
                                  <Button variant="ghost" size="sm" title="Remove" onClick={() => del(i)}>🗑️</Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══ ADD CHECKLIST ═════════════════════════════════════════════════════ */}
      <Modal open={modal} onClose={() => setModal(false)} title="Add Items to Vehicle" size="xl">
        <form onSubmit={save} className="space-y-4">
          <Select label="Vehicle *" value={vehicleId} onChange={e => setVehicleId(e.target.value)} required>
            <option value="">Select vehicle…</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number} — {v.brand} {v.model}</option>)}
          </Select>

          <div className="border rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600 uppercase">Items — tick what the vehicle has</div>
            <div className="max-h-80 overflow-y-auto divide-y">
              {rows.map((r, i) => (
                <div key={i} className={`px-4 py-2 ${r.checked ? 'bg-primary-50/40' : ''}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <input type="checkbox" checked={r.checked}
                      onChange={e => setRow(i, { checked: e.target.checked })} className="w-4 h-4" />
                    {STANDARD_ITEMS.includes(r.item_name) ? (
                      <span className="w-44 text-sm font-medium">{r.item_name}</span>
                    ) : (
                      <input className="w-44 border rounded px-2 py-1 text-sm" placeholder="Item name"
                        value={r.item_name} onChange={e => setRow(i, { item_name: e.target.value })} />
                    )}
                    <input type="number" min="1" className="w-16 border rounded px-2 py-1 text-sm"
                      value={r.quantity} onChange={e => setRow(i, { quantity: e.target.value })} title="Quantity" />
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={r.branded}
                        onChange={e => setRow(i, { branded: e.target.checked })} /> Branded
                    </label>
                    {r.branded && (
                      <input className="w-32 border rounded px-2 py-1 text-sm" placeholder="Brand"
                        value={r.brand_name} onChange={e => setRow(i, { brand_name: e.target.value })} />
                    )}
                    <input type="number" step="0.01" className="w-28 border rounded px-2 py-1 text-sm"
                      placeholder="Amount Rs." value={r.amount} onChange={e => setRow(i, { amount: e.target.value })} />
                    <select className="border rounded px-2 py-1 text-sm" value={r.condition_st}
                      onChange={e => setRow(i, { condition_st: e.target.value })}>
                      <option value="good">Good</option>
                      <option value="damaged">Damaged</option>
                      <option value="missing">Missing</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 bg-gray-50">
              <button type="button" onClick={addCustomRow} className="text-sm text-primary-600 hover:underline">+ Add another item</button>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Items'}</Button>
          </div>
        </form>
      </Modal>

      {/* ══ EDIT ITEM ═════════════════════════════════════════════════════════ */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit — ${editing?.item_name || ''}`}>
        {editing && (
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input label="Item Name" value={editForm.item_name}
                onChange={e => setEditForm({ ...editForm, item_name: e.target.value })} />
              <Input label="Quantity" type="number" min="1" value={editForm.quantity}
                onChange={e => setEditForm({ ...editForm, quantity: e.target.value })} />
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={editForm.branded}
                  onChange={e => setEditForm({ ...editForm, branded: e.target.checked })} />
                <span className="text-sm">Branded</span>
              </div>
              {editForm.branded && (
                <Input label="Brand" value={editForm.brand_name}
                  onChange={e => setEditForm({ ...editForm, brand_name: e.target.value })} />
              )}
              <Input label="Amount (Rs.)" type="number" step="0.01" value={editForm.amount}
                onChange={e => setEditForm({ ...editForm, amount: e.target.value })} />
              <Select label="Condition" value={editForm.condition_st}
                onChange={e => setEditForm({ ...editForm, condition_st: e.target.value })}>
                <option value="good">Good</option>
                <option value="damaged">Damaged</option>
                <option value="missing">Missing</option>
              </Select>
            </div>
            <Input label="Notes" value={editForm.notes}
              onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
